import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Bell, Trash2, Mail, CheckCircle2, Clock, ExternalLink, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminStockNotifications = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [groups, setGroups] = useState([]);
  const [totals, setTotals] = useState({ total_subscriptions: 0, total_products: 0 });
  const [statusFilter, setStatusFilter] = useState('pending'); // all | pending | notified
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/stock-notifications?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setGroups(data.groups || []);
        setTotals({
          total_subscriptions: data.total_subscriptions || 0,
          total_products: data.total_products || 0,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getProductName = (name) => {
    if (!name || typeof name !== 'object') return '—';
    return name[i18n.language] || name.sk || name.en || Object.values(name)[0] || '—';
  };

  const deleteSub = async (subId) => {
    if (!window.confirm(t('admin.stock_notifications.confirm_delete'))) return;
    const res = await fetch(`${API_URL}/api/admin/stock-notifications/${subId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) fetchData();
  };

  const toggle = (pid) => setExpanded((prev) => ({ ...prev, [pid]: !prev[pid] }));

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stock-notifications/export?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = res.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : 'sunify_stock_subscribers.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed', err);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString(i18n.language);
    } catch {
      return iso;
    }
  };

  return (
    <div className="max-w-6xl" data-testid="admin-stock-notifications-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
            <Bell className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.stock_notifications.title')}</h1>
            <p className="text-sm text-gray-500">{t('admin.stock_notifications.subtitle')}</p>
          </div>
        </div>
        <button
          onClick={exportCsv}
          disabled={totals.total_subscriptions === 0}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
          data-testid="export-csv-button"
        >
          <Download className="h-4 w-4" />
          {t('admin.stock_notifications.export_csv')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-sm text-gray-500">{t('admin.stock_notifications.total_subs')}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{totals.total_subscriptions}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-sm text-gray-500">{t('admin.stock_notifications.products_watched')}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{totals.total_products}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-sm text-gray-500">{t('admin.stock_notifications.filter_label')}</p>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            data-testid="stock-notif-filter"
          >
            <option value="pending">{t('admin.stock_notifications.status_pending')}</option>
            <option value="notified">{t('admin.stock_notifications.status_notified')}</option>
            <option value="all">{t('admin.stock_notifications.status_all')}</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" />
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Bell className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">{t('admin.stock_notifications.empty')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => {
            const isOpen = !!expanded[g.product_id];
            return (
              <div key={g.product_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`stock-group-${g.product_id}`}>
                <button
                  onClick={() => toggle(g.product_id)}
                  className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition text-left"
                  data-testid={`toggle-${g.product_id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold text-gray-900">{getProductName(g.name)}</span>
                      {g.sku && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">{g.sku}</span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                        g.stock > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        stock: {g.stock ?? '—'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-orange-500" />
                        {g.pending_count} {t('admin.stock_notifications.pending')}
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        {g.notified_count} {t('admin.stock_notifications.notified')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={`/product/${g.product_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-gray-400 hover:text-orange-500 p-2"
                      title={t('admin.stock_notifications.open_product')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    <span className="text-gray-400 text-xl">{isOpen ? '−' : '+'}</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 px-5 py-3">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-100">
                          <th className="py-2 font-medium">Email</th>
                          <th className="py-2 font-medium">{t('admin.stock_notifications.language')}</th>
                          <th className="py-2 font-medium">{t('admin.stock_notifications.created_at')}</th>
                          <th className="py-2 font-medium">{t('admin.stock_notifications.status')}</th>
                          <th className="py-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.subscriptions.map((s) => (
                          <tr key={s.id} className="border-b border-gray-50 last:border-0" data-testid={`sub-row-${s.id}`}>
                            <td className="py-2.5">
                              <a href={`mailto:${s.email}`} className="flex items-center gap-2 text-gray-800 hover:text-orange-500">
                                <Mail className="h-4 w-4 text-gray-400" />
                                {s.email}
                              </a>
                            </td>
                            <td className="py-2.5 text-gray-600 uppercase font-medium text-xs">{s.language}</td>
                            <td className="py-2.5 text-gray-500">{formatDate(s.created_at)}</td>
                            <td className="py-2.5">
                              {s.notified ? (
                                <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded text-xs">
                                  <CheckCircle2 className="h-3 w-3" />
                                  {t('admin.stock_notifications.notified')}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-orange-700 bg-orange-50 px-2 py-0.5 rounded text-xs">
                                  <Clock className="h-3 w-3" />
                                  {t('admin.stock_notifications.pending')}
                                </span>
                              )}
                            </td>
                            <td className="py-2.5">
                              <button
                                onClick={() => deleteSub(s.id)}
                                className="text-gray-400 hover:text-red-500 transition"
                                title={t('admin.stock_notifications.delete')}
                                data-testid={`delete-sub-${s.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminStockNotifications;
